import type {
  DraftTaskAttachment,
  DraftTaskScopeFailureReason,
  LocalFile,
  SavedFile,
  SavedFileFields,
  TaskInitialQuestionResponse,
  TypedFormData,
} from '@frontend/api-types';
import { match } from '@frontend/duck-tape';
import { makeSavedFileList } from '@frontend/forms';

export const getScopeFailureExplanationCopy = (reason: DraftTaskScopeFailureReason) =>
  match(reason)
    .with(
      'exceeds_time_window',
      () => 'This task exceeds our time window. Please try breaking it down into smaller tasks.',
    )
    .with(
      'multi_task',
      () => 'This appears to be multiple tasks. Please change the description and submit each task separately.',
    )
    .with('out_of_scope', () => 'This task is outside our scope of services.')
    .exhaustive();

// Api doesn't return the fields as we expect SavedFile objects to look like, so we have to add them manually
export type DraftTaskAttachmentList = (LocalFile | SavedFile<DraftTaskAttachment & SavedFileFields>)[];

export const convertAttachmentUrlsToAttachmentsList = (
  attachmentUrls: DraftTaskAttachment[],
): DraftTaskAttachmentList => makeSavedFileList(attachmentUrls, (a) => ({ ...a, key: a.id, url: a.contentUrl }));

export const handleAttachmentsForFormData = <
  TFormData extends TypedFormData<{
    attachments: DraftTaskAttachmentList;
    // This is a list of attachment ids to delete
    delete_attachments?: string[];
  }>,
>({
  attachments,
  formData,
  originalAttachments,
}: {
  attachments: DraftTaskAttachmentList;
  formData: TFormData;
  originalAttachments: DraftTaskAttachment[];
}) => {
  const localFileAttachments = attachments.filter((a) => a.__typename === 'LocalFile') as LocalFile[];
  if (localFileAttachments.length) {
    // Only add attachments that are new local files, which should be uploaded
    formData.appendLocalFiles('attachments', localFileAttachments);
  }

  const keptAttachmentUrls = attachments.reduce(
    (acc, cur) => (cur.__typename === 'SavedFile' ? [...acc, cur.contentUrl] : acc),
    [] as string[],
  );
  const deleteAttachmentIds = originalAttachments
    .filter((a) => !keptAttachmentUrls.includes(a.contentUrl))
    .map((a) => a.id);

  // Delete attachments that have been removed from the existing attachment urls
  if (deleteAttachmentIds.length) {
    formData.append('delete_attachments', deleteAttachmentIds);
  }
  return formData;
};

export const isLastInitialQuestion = (
  initialQuestionsWithAnswer: Pick<TaskInitialQuestionResponse, 'type'> & { question?: string },
) =>
  match(initialQuestionsWithAnswer)
    .with({ type: 'text' }, ({ question }) => question === 'Anything else we should know?')
    .otherwise(() => false);

export const SECURE_NOTE_IQ_TITLE =
  "It's likely that our copilots will need this information to service this task, would you like to share it? It may speed up the process.";
